import { exploreSchemaItemList } from '@/templates/Landing/seoLandings/exploreSchema';
import { ENV } from '@/constants';

export const schemaObject_AiArtGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-art-generator',
			name: 'AI Art Generator — Turn Photos Into AI Art Online | Lift',
			description:
				"Create stunning AI Art from your photos instantly with Lift's AI Art generator. No watermark, no design skills needed — just upload and transform. Perfect for social media, prints, and creative projects.",
			url: 'https://liftapp.ai/ai-art-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-art-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-art-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-art-generator#product',
			name: 'AI Art Generator Online',
			description:
				"Create stunning AI-generated art from your photos instantly with Lift's AI art generator. No watermark, no design skills needed — just upload and transform. Perfect for social media, prints, and creative projects.",
			url: 'https://liftapp.ai/ai-art-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-art-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'We create a custom AI style',
					text: 'Instantly convert your photo into stunning art',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-art-generator#benefits',
			name: 'Who can benefit from AI Art Generator?',
			description:
				"Lift's AI Art Generator is useful for creators, fans, artists, social media users, business owners, and content creators who want to transform photos into stunning AI-generated artwork.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Creators needing unique album or cover art',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Digital artists experimenting with fresh AI styles',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Social media users boosting engagement with stylized posts',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small businesses designing branded visuals or merch',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Content creators needing quick concept art or thumbnails',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'Fans transforming portraits into collectible prints',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Art Generator by Lift',
				description:
					'Example of how Lift AI Art Generator transforms photos into stunning AI-generated artwork.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-art-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Art Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Art Generator is an online tool that transforms your photos into stunning AI-generated artwork — perfect for social media, prints, digital galleries, or creative projects.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Art Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI analyzes your photo with deep learning, keeps your facial features accurate, and reimagines them in art style with artistic backgrounds, colors, and details. Everything is automatic — no manual editing needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I convert any photo into AI art?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift works with portraits, landscapes, pets, objects, and any image type. The AI adapts to your photo's content and applies artistic transformations that enhance composition, color, and style while keeping the essence of your original image.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-art-generator#why-lift',
			name: "Why Lift's AI Art Generator stands out",
			description:
				"Lift's AI Art Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Art Generator',
				description:
					"Comparison of Lift's AI Art Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-art-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-art-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Art Generator',
					item: 'https://liftapp.ai/ai-art-generator',
				},
			],
		},
	],
};
